// Création d'un compte et invitation dans l’organisation ; si le compte existe déjà,
// proposition d'inviter ce compte existant (D-026).
import { Bandeau, Bouton, Champ, Formulaire, GrilleChamps, useBrouillon } from '@eec/ui';
import { useState } from 'react';
import { requete } from '../../api/client';
import { ErreurApi } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';

interface Saisie {
  identifiant: string;
  nom: string;
  prenoms: string;
  courriel: string;
  telephone: string;
}

const VIDE: Saisie = { identifiant: '', nom: '', prenoms: '', courriel: '', telephone: '' };

export function FormulaireUtilisateur({ onTermine }: { onTermine: () => void }) {
  const [saisie, setSaisie] = useState<Saisie>(VIDE);
  // Le mot de passe initial n'est jamais placé dans le brouillon.
  const [motDePasse, setMotDePasse] = useState('');
  const brouillon = useBrouillon('utilisateur-nouveau', saisie);
  const [compteExistant, setCompteExistant] = useState<string | null>(null);

  const creation = useAction({
    executer: (s: Saisie & { mot_de_passe_initial: string }) =>
      requete<{ id: string; identifiant: string }>('/utilisateurs', {
        methode: 'POST',
        corps: Object.fromEntries(Object.entries(s).filter(([, v]) => v !== '')),
      }),
    succes: (r) => `Compte ${r.identifiant} créé et invité : l'utilisateur doit accepter l'invitation à sa première connexion.`,
    invalider: [['utilisateurs']],
    apres: () => {
      brouillon.effacer();
      onTermine();
    },
  });

  const invitation = useAction({
    executer: (compteId: string) => requete(`/utilisateurs/${compteId}/appartenances`, { methode: 'POST', corps: { motif: 'Invitation par l\'administrateur local' } }),
    succes: 'Invitation envoyée : elle apparaîtra à l\'utilisateur lors de sa prochaine connexion.',
    invalider: [['utilisateurs']],
    apres: () => {
      brouillon.effacer();
      onTermine();
    },
  });

  const erreurLocale = (champ: string) =>
    creation.erreursChamps.find((e) => e.champ === champ || e.champ === champ.replace('-', '_'))?.message ?? null;

  const soumettre = () => {
    setCompteExistant(null);
    creation
      .lancerAsync({ ...saisie, mot_de_passe_initial: motDePasse })
      .catch((e: unknown) => {
        if (e instanceof ErreurApi && e.code === 'compte_existant') {
          setCompteExistant((e.details as { compte_id?: string } | undefined)?.compte_id ?? null);
        }
      });
  };

  const maj = (cle: keyof Saisie) => (e: { target: { value: string } }) => setSaisie((s) => ({ ...s, [cle]: e.target.value }));

  return (
    <Formulaire
      libelle="Nouvel utilisateur"
      onSoumettre={soumettre}
      erreurs={creation.erreursChamps.map((e) => ({ champ: `utilisateur-${e.champ.replace('_', '-')}`, message: e.message }))}
      brouillonEnregistreLe={brouillon.enregistreLe}
      restauration={
        brouillon.brouillonDisponible
          ? {
              date: brouillon.brouillonDate,
              onReprendre: () => {
                setSaisie({ ...VIDE, ...brouillon.brouillonDisponible });
                brouillon.ignorer();
              },
              onIgnorer: brouillon.effacer,
            }
          : null
      }
      actions={
        <>
          <Bouton type="submit" chargement={creation.enCours} libelleChargement="Création en cours">
            Créer et inviter
          </Bouton>
          <Bouton variante="secondaire" onClick={onTermine}>
            Annuler
          </Bouton>
        </>
      }
    >
      {creation.messageErreur && creation.erreursChamps.length === 0 && !compteExistant ? <Bandeau type="erreur">{creation.messageErreur}</Bandeau> : null}
      {compteExistant ? (
        <Bandeau type="attention" titre="Ce compte existe déjà">
          <p>Un compte Eau Énergie Connect porte déjà cet identifiant. Vous pouvez l&apos;inviter dans votre organisation sans en créer un nouveau.</p>
          <div style={{ marginTop: 8 }}>
            <Bouton petit chargement={invitation.enCours} onClick={() => invitation.lancer(compteExistant)}>
              Inviter ce compte
            </Bouton>
          </div>
          {invitation.messageErreur ? <p role="alert">{invitation.messageErreur}</p> : null}
        </Bandeau>
      ) : null}
      <GrilleChamps>
        <Champ id="utilisateur-identifiant" libelle="Identifiant de connexion" aide="Courriel ou téléphone" value={saisie.identifiant} onChange={maj('identifiant')} erreur={erreurLocale('identifiant')} required />
        <Champ id="utilisateur-nom" libelle="Nom" value={saisie.nom} onChange={maj('nom')} erreur={erreurLocale('nom')} required />
        <Champ id="utilisateur-prenoms" libelle="Prénoms" value={saisie.prenoms} onChange={maj('prenoms')} facultatif />
        <Champ id="utilisateur-courriel" libelle="Courriel" type="email" value={saisie.courriel} onChange={maj('courriel')} erreur={erreurLocale('courriel')} facultatif />
        <Champ id="utilisateur-telephone" libelle="Téléphone" type="tel" value={saisie.telephone} onChange={maj('telephone')} erreur={erreurLocale('telephone')} facultatif />
        <Champ
          id="utilisateur-mot-de-passe-initial"
          libelle="Mot de passe initial"
          type="password"
          autoComplete="new-password"
          aide="12 caractères au moins, avec une lettre et un chiffre ; à communiquer à l'utilisateur par un canal sûr"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          erreur={erreurLocale('mot_de_passe_initial')}
          required
        />
      </GrilleChamps>
    </Formulaire>
  );
}
